/**
 * Smile region extraction — build lip polygons and convex hull from mouth landmarks.
 */

import {
  getInnerLipPoints,
  getMouthPoints,
  getOuterLipPoints,
} from "./landmarkProcessing";
import { SMILE_CROP_PADDING_X, SMILE_CROP_PADDING_Y } from "./constants";
import type { BoundingBox, Point2D, SmileRegion } from "./types";

/** Andrew's monotone chain convex hull */
export function convexHull(points: Point2D[]): Point2D[] {
  if (points.length <= 2) return [...points];

  const sorted = [...points].sort((a, b) =>
    a.x === b.x ? a.y - b.y : a.x - b.x
  );

  const cross = (o: Point2D, a: Point2D, b: Point2D) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const lower: Point2D[] = [];
  for (const p of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }

  const upper: Point2D[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }

  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

export function boundingBoxFromPoints(
  points: Point2D[],
  padding: number | { x?: number; y?: number } = 0
): BoundingBox {
  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }

  const w = maxX - minX;
  const h = maxY - minY;
  const padXRatio = typeof padding === "number" ? padding : (padding.x ?? 0);
  const padYRatio = typeof padding === "number" ? padding : (padding.y ?? 0);
  const padX = w * padXRatio;
  const padY = h * padYRatio;

  return {
    x: minX - padX,
    y: minY - padY,
    width: w + padX * 2,
    height: h + padY * 2,
  };
}

export function clampBoundingBox(
  box: BoundingBox,
  imageWidth: number,
  imageHeight: number
): BoundingBox {
  const x = Math.max(0, Math.floor(box.x));
  const y = Math.max(0, Math.floor(box.y));
  const right = Math.min(imageWidth, Math.ceil(box.x + box.width));
  const bottom = Math.min(imageHeight, Math.ceil(box.y + box.height));
  return {
    x,
    y,
    width: Math.max(1, right - x),
    height: Math.max(1, bottom - y),
  };
}

/**
 * Build smile region from full-face pixel landmarks (length ≥ 468).
 */
export function extractSmileRegion(
  pixelLandmarks: Point2D[],
  imageWidth: number,
  imageHeight: number,
  cropPadding:
    | number
    | { x?: number; y?: number } = {
    x: SMILE_CROP_PADDING_X,
    y: SMILE_CROP_PADDING_Y,
  }
): SmileRegion | null {
  if (pixelLandmarks.length < 100) return null;

  const outerRaw = getOuterLipPoints(pixelLandmarks);
  const innerRaw = getInnerLipPoints(pixelLandmarks);
  const mouth = getMouthPoints(pixelLandmarks);

  if (outerRaw.length < 3 || innerRaw.length < 3) return null;

  // Keep MediaPipe ring order — angle-sorting collapses the vermilion into the mouth opening
  const outerPolygon = outerRaw.map((p) => ({ ...p }));
  const innerPolygon = innerRaw.map((p) => ({ ...p }));
  const hull = convexHull(mouth);
  const box = clampBoundingBox(
    boundingBoxFromPoints([...mouth, ...outerPolygon], cropPadding),
    imageWidth,
    imageHeight
  );

  return {
    outerPolygon,
    innerPolygon,
    convexHull: hull,
    boundingBox: box,
    mouthLandmarks: mouth,
  };
}
